import { useCallback, useMemo, useState } from "react";
import { getPaperStableId } from "../../../shared/utils/paper";
import { MAX_PAPER_ANALYSIS_SOURCES } from "../constants/paperAnalysis";

export const useContextPapers = () => {
  const [contextPapers, setContextPapers] = useState(new Map());

  const toggleContextPaper = useCallback((paper) => {
    const paperId = getPaperStableId(paper);
    if (!paperId) return;

    setContextPapers((prev) => {
      const next = new Map(prev);
      if (prev.has(paperId)) {
        next.delete(paperId);
        return next;
      }
      if (next.size >= MAX_PAPER_ANALYSIS_SOURCES) {
        return prev;
      }
      next.set(paperId, { ...paper, id: paperId });
      return next;
    });
  }, []);

  const removeContextPaper = useCallback((paperId) => {
    if (!paperId) return;
    setContextPapers((prev) => {
      const next = new Map(prev);
      next.delete(String(paperId));
      return next;
    });
  }, []);

  const clearContextPapers = useCallback(() => {
    setContextPapers(new Map());
  }, []);

  const addContextPapers = useCallback((papers = []) => {
    setContextPapers((prev) => {
      const next = new Map(prev);
      papers.forEach((paper) => {
        if (next.size >= MAX_PAPER_ANALYSIS_SOURCES) {
          return;
        }
        const paperId = getPaperStableId(paper);
        if (paperId) {
          next.set(paperId, { ...paper, id: paperId });
        }
      });
      return next;
    });
  }, []);

  const contextPaperList = useMemo(
    () => Array.from(contextPapers.values()),
    [contextPapers],
  );

  const contextPaperIds = useMemo(() => new Set(contextPapers.keys()), [contextPapers]);

  return {
    contextPapers,
    contextPaperList,
    contextPaperIds,
    contextPaperCount: contextPapers.size,
    maxContextPaperCount: MAX_PAPER_ANALYSIS_SOURCES,
    setContextPapers,
    toggleContextPaper,
    removeContextPaper,
    clearContextPapers,
    addContextPapers,
  };
};

export default useContextPapers;
